import isString from 'lodash-es/isString'

import { EMPTY_LIST, EMPTY_STRING } from '../../constants/common'
import {
  EMAIL_INFO_ACCOUNT,
  EMAIL_REPLY_FORM_HTML_BLOCK_ID,
  EMAIL_REPLY_FORM_HTML_QUOTED_HISTORY_CLASSNAME,
} from '../../constants/email'
import { REG_EXP_EMAIL } from '../../constants/regExp'
import { LOCAL_STORAGE_MASS_MAILING_FROM_CREATE } from '../../constants/storage'
import i18n from '../../i18n'
import { newLineToBr } from '../../utils/common'
import { isServiceEmailAccount } from '../../utils/email'
import { toDocument } from '../../utils/html'
import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../utils/massActions'
import { getStorageItem, setStorageItem } from '../../utils/storage'
import { getUserAvatar, isDwellerRole } from '../../utils/user'
import { isRealtimeEvent } from '../Centrifuge'
import { isDraftCreateNewAction } from '../Draft'
import {
  REQUEST_FORM_FIELD_NAME_APPLICANT,
  REQUEST_FORM_FIELD_NAME_EMAIL_MESSAGE,
  REQUEST_FORM_INITIAL_VALUES,
} from '../Request'
import { isContractorUserAccount, toUserSelectItem } from '../User'

import {
  EMAIL_ACCOUNTS_API_PAGE_SIZE,
  EMAIL_ACTION_ADD_NOTE,
  EMAIL_ACTION_ARCHIVE_ITEM,
  EMAIL_ACTION_ARCHIVE_MASS,
  EMAIL_ACTION_ATTACH_REQUEST,
  EMAIL_ACTION_ATTACH_REQUEST_MASS,
  EMAIL_ACTION_COPY_TO_ARCHIVE_ITEM,
  EMAIL_ACTION_COPY_TO_ARCHIVE_MASS,
  EMAIL_ACTION_CREATE_SERVICE_REQUEST,
  EMAIL_ACTION_EDIT_LABELS_ITEM,
  EMAIL_ACTION_EDIT_LABELS_MASS,
  EMAIL_ACTION_MARK_AS_READ_ITEM,
  EMAIL_ACTION_MARK_AS_READ_MASS,
  EMAIL_ACTION_MARK_AS_STARRED_ITEM,
  EMAIL_ACTION_MARK_AS_STARRED_MASS,
  EMAIL_ACTION_MARK_AS_UNREAD_ITEM,
  EMAIL_ACTION_MARK_AS_UNREAD_MASS,
  EMAIL_ACTION_MARK_AS_UNSTARRED_ITEM,
  EMAIL_ACTION_MARK_AS_UNSTARRED_MASS,
  EMAIL_ACTION_OPEN_IN_NEW_TAB,
  EMAIL_ACTION_RESTORE_ITEM,
  EMAIL_ACTION_RESTORE_MASS,
  EMAIL_MESSAGE_COMPLETED_TABLE_RELOAD,
  EMAIL_NO_REPLY_PREFIXES,
  EMAIL_PROFILE_ACTION_FORWARD,
  EMAIL_PROFILE_ACTION_PRINT,
  EMAIL_PROFILE_ACTION_REPLY,
  EMAIL_PROFILE_ACTION_REPLY_ALL,
  EMAIL_REALTIME_EVENT,
  EMAIL_RECIPIENT_OPTION_ID_SEPARATOR,
  EMAIL_TABLE_ROW_ACTIONS_CONFIG,
  EMAIL_TABLE_ROW_MESSAGE_STATUS_ID,
} from './Email.constants'
import { EmailSubMenuParam } from './Email.enums'

import ProviderIcon from '../../icons/provider.svg'

function setEmailStorageDontShowValue(value) {
  setStorageItem(LOCAL_STORAGE_MASS_MAILING_FROM_CREATE, !value)
}

function toEmailThreadsModalTableRowParticipant(recipient) {
  return { ...recipient, fullname: recipient.name }
}

function getThreadRowCompletedUpdates(threadRow, files) {
  return {
    ...(threadRow.last_message
      ? {
          last_message: {
            ...threadRow.last_message,
            ...(files ? { files: files.map(toCompletedMessageFile) } : null),
            completed: true,
          },
        }
      : null),
    completed: true,
    has_pending_message: false,
  }
}

function toCompletedMessageFile(file) {
  return file.filename ? file : { ...file, filename: file.name }
}

function getMessageRowCompletedUpdate(messageRow, { error, files }) {
  return {
    id: messageRow.uuid,
    updates: {
      ...(files ? { files: files.map(toCompletedMessageFile) } : null),
      completed: true,
      error: error ?? null,
    },
  }
}

function getThreadRowMessageCompletedUpdate(threadRow, { error, files, uuid }) {
  const isLastMessageEvent =
    !threadRow.last_message || threadRow.last_message.uuid === uuid

  if (error) {
    return {
      id: threadRow.uuid,
      updates: {
        ...(isLastMessageEvent
          ? getThreadRowCompletedUpdates(threadRow, files)
          : null),
        last_error: error,
      },
    }
  }

  if (threadRow.last_error) {
    return EMAIL_MESSAGE_COMPLETED_TABLE_RELOAD
  }

  if (!isLastMessageEvent) {
    return null
  }

  return {
    id: threadRow.uuid,
    updates: {
      ...getThreadRowCompletedUpdates(threadRow, files),
      last_error: null,
    },
  }
}

export function isEmailCorporate(email) {
  return email.corporate
}

export function isEmailShared(email) {
  return email.shared && !email.is_own
}

export function isEmailArchiveSub(sub) {
  return sub === EmailSubMenuParam.Archive
}

export function isEmailTrashSub(sub) {
  return sub === EmailSubMenuParam.Trash
}

export function isEmailArchiveOrTrashSub(sub) {
  return isEmailArchiveSub(sub) || isEmailTrashSub(sub)
}

export function isEmailAccountsApiPageFull(items) {
  return items.length === EMAIL_ACCOUNTS_API_PAGE_SIZE
}

export function getEmailApiEntityType(singleEmail) {
  return singleEmail ? 'no-thread-view-email-messages' : 'email-threads'
}

export function isEmailContactRecipient(item) {
  return Number.isSafeInteger(item.realId) || Number.isSafeInteger(item.value)
}

export function isEmailNonContactRecipient(item) {
  // if the email account is not a contact, the id contains the value
  // of email (string)
  return isString(item.value) && item.value.length > 0
}

export function isEmailRecipient(item) {
  return isEmailContactRecipient(item) || isEmailNonContactRecipient(item)
}

export function getEmailRecipientsCorrectValue(list) {
  return list.filter(isEmailRecipient)
}

export function checkEmailForReply(item) {
  if (!item || !REG_EXP_EMAIL.test(item)) {
    return false
  }

  return !EMAIL_NO_REPLY_PREFIXES.some(prefix => item.startsWith(prefix))
}

export function getEmailCreationSuccessNotificationProps(data) {
  const storageValue = getStorageItem(LOCAL_STORAGE_MASS_MAILING_FROM_CREATE)

  if (data.mass && storageValue !== 'false') {
    return {
      checkbox: true,
      checkboxProps: {
        label: i18n.t('Email.DontShowAgainKey'),
        onChange: setEmailStorageDontShowValue,
      },
      text: i18n.t('Email.EmailSentMassMailingTextNotificationKey'),
      title: i18n.t('Email.EmailSentTitleKey'),
    }
  }

  return 'Email.EmailSentTitleKey'
}

export function getEmailAccountForCreate(activeAccounts) {
  if (activeAccounts?.length === 0) {
    return null
  }

  return activeAccounts?.[0]
}

export function getEmailThreadMessageItem(item, withTags) {
  return {
    ...(withTags ? { notes: item.notes } : null),
    is_high_priority: item.is_high_priority,
    last_message: {
      // only not for form creation
      ...(withTags ? { files: item.last_message.files } : null),
      is_high_priority: item.last_message.is_high_priority,
      text_preview: item.last_message.text,
    },
    subject: item.title,
  }
}

export function getEmailThreadDateItem(item) {
  return {
    last_message_created: item.last_message.created,
  }
}

export function getEmailParticipants(email) {
  return (email.outgoing ? email.recipients : [email.owner_obj]).map(
    toEmailThreadsModalTableRowParticipant
  )
}

// Archived copies carry no `linked_requests`: the request of the original
// message arrives as `original_for_archive.request` instead.
export function getEmailLinkedRequests({
  linked_requests: linkedRequests,
  original_for_archive: originalForArchive,
}) {
  if (linkedRequests?.length > 0) {
    return linkedRequests
  }

  return originalForArchive?.request ? [originalForArchive.request] : EMPTY_LIST
}

export function getEmailTableRowMessageStatusSending(t) {
  return {
    id: EMAIL_TABLE_ROW_MESSAGE_STATUS_ID.sending,
    text: t('EmailTable.MessageStatusSendingKey'),
    textColor: 'var(--color-info)',
    tooltip: t('EmailTable.MessageStatusSendingTooltipKey'),
  }
}

export function getEmailTableRowMessageStatusError({ t, tooltip }) {
  return {
    id: EMAIL_TABLE_ROW_MESSAGE_STATUS_ID.error,
    text: t('EmailTable.MessageStatusErrorKey'),
    textColor: 'var(--color-error)',
    tooltip,
  }
}

export function getEmailMessageStatuses({ error, sending, t }) {
  return [
    ...(sending ? [getEmailTableRowMessageStatusSending(t)] : EMPTY_LIST),
    ...(error
      ? [
          getEmailTableRowMessageStatusError({
            t,
            tooltip: error.is_persistent ? t(error.title) : error.title,
          }),
        ]
      : EMPTY_LIST),
  ]
}

export function isEmailMessageCompletedEvent(ctx) {
  return isRealtimeEvent(ctx, EMAIL_REALTIME_EVENT.MessageCompleted)
}

export function isEmailMessageCompletedTableReload(update) {
  return update === EMAIL_MESSAGE_COMPLETED_TABLE_RELOAD
}

export function isEmailMessagePending(message) {
  return message?.completed === false
}

export function isEmailHighPriority(item) {
  return Boolean(item?.is_high_priority || item?.last_message?.is_high_priority)
}

export function getEmailMessageCompletedTableUpdate(items, payload) {
  const messageRow = items?.find(item => item.uuid === payload.uuid)

  if (messageRow) {
    return getMessageRowCompletedUpdate(messageRow, payload)
  }

  const threadRow = items?.find(item => item.uuid === payload.thread_uuid)

  return threadRow
    ? getThreadRowMessageCompletedUpdate(threadRow, payload)
    : null
}

export function hasEmailListPendingItem(items) {
  return Boolean(
    items?.some(
      item =>
        item.has_pending_message ||
        isEmailMessagePending(item.last_message ?? item)
    )
  )
}

export function getEmailMessagesWithMessageCompleted(
  messages,
  { error, files, uuid }
) {
  if (!messages) {
    return messages
  }

  return messages.map(message =>
    message.uuid === uuid
      ? {
          ...message,
          ...(files ? { files: files.map(toCompletedMessageFile) } : null),
          completed: true,
          error: error ?? null,
        }
      : message
  )
}

export function getEmailDraftId(prefix) {
  return prefix
}

export function clearEmailDraftIfCreateNew(action, setDraft) {
  if (isDraftCreateNewAction(action)) {
    setDraft(null, 0)
  }
}

export function parseEmailsFromInput(input) {
  return input.split(/[;,\s]+/).filter(Boolean)
}

export function getEmailAccountLabel(account) {
  if (account.description === EMAIL_INFO_ACCOUNT) {
    return account.outgoing?.email_from
  }

  if (isServiceEmailAccount(account)) {
    return (
      account.email_from_name || account.outgoing?.email_from || account.email
    )
  }

  return account.email_from_name || account.email
}

export function getEmailCreationFormInitialAccount(account) {
  return account
    ? { ...account, label: getEmailAccountLabel(account), value: account.id }
    : null
}

export function getEmailServiceRequestCreationInitialValues(item) {
  const { owner } = item

  const applicantInitialValue = isDwellerRole(owner)
    ? {
        [REQUEST_FORM_FIELD_NAME_APPLICANT]: toUserSelectItem(owner),
      }
    : null

  return {
    ...REQUEST_FORM_INITIAL_VALUES,
    ...applicantInitialValue,
    [REQUEST_FORM_FIELD_NAME_EMAIL_MESSAGE]:
      item[EMAIL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey],
  }
}

export function normalizeEmail(email) {
  if (!isString(email)) {
    return EMPTY_STRING
  }

  return email.trim().toLowerCase()
}

export function buildEmailRecipientOptionId({ email, id }) {
  const normalized = normalizeEmail(email)

  if (Number.isSafeInteger(id)) {
    return `${id}${EMAIL_RECIPIENT_OPTION_ID_SEPARATOR}${normalized}`
  }

  return normalized
}

export function toEmailRecipientSelectItem(item) {
  const optionId = buildEmailRecipientOptionId({
    email: item.email,
    id: item.id,
  })
  const realId = Number.isSafeInteger(item?.id) ? item.id : null

  return {
    ...toUserSelectItem(item),
    additionalData: item.email,
    avatar: {
      ...getUserAvatar(item),
      Icon: isContractorUserAccount(item) ? ProviderIcon : null,
    },
    emails: item.emails,
    emailType: item.type,
    isContractor: isContractorUserAccount(item),
    optionId,
    realId,
    value: realId !== null ? optionId : item.email || item.id,
  }
}

export function toEmailRecipientSelectItems(
  items,
  mapper = toEmailRecipientSelectItem
) {
  return items.map(mapper)
}

export function newLineToBrInEmailText(text) {
  const source = isString(text) ? text : EMPTY_STRING
  const looksLikeHtml = source.startsWith('<') && source.endsWith('>')

  return looksLikeHtml ? source : newLineToBr(source)
}

export function hasQuotedHistoryBlock(html) {
  if (!html) {
    return false
  }

  const reply = toDocument(html).getElementById(EMAIL_REPLY_FORM_HTML_BLOCK_ID)

  return (
    reply !== null &&
    reply.classList.contains(EMAIL_REPLY_FORM_HTML_QUOTED_HISTORY_CLASSNAME)
  )
}

export function getEmailTableNotificationTitle({
  action,
  count,
  t,
  totalCount,
}) {
  const types = {
    [EMAIL_ACTION_ADD_NOTE]: t('Notification.EmailTitleAddNoteKey'),
    [EMAIL_ACTION_ARCHIVE_ITEM]: t('Notification.CommonTitleArchivedKey'),
    [EMAIL_ACTION_ARCHIVE_MASS]: t('Notification.CommonTitleMassArchivedKey', {
      count,
      totalCount,
    }),
    [EMAIL_ACTION_ATTACH_REQUEST]: t('Notification.CommonTitleAttachedKey'),
    [EMAIL_ACTION_ATTACH_REQUEST_MASS]: t(
      'Notification.CommonTitleMassAttachedKey',
      {
        count,
        totalCount,
      }
    ),
    [EMAIL_ACTION_COPY_TO_ARCHIVE_ITEM]: t('Notification.EmailTitleCopyToKey'),
    [EMAIL_ACTION_COPY_TO_ARCHIVE_MASS]: t(
      'Notification.EmailTitleMassCopyToKey',
      {
        count,
        totalCount,
      }
    ),
    [EMAIL_ACTION_EDIT_LABELS_ITEM]: t('Notification.CommonTitleSavedKey'),
    [EMAIL_ACTION_EDIT_LABELS_MASS]: t('Notification.CommonTitleMassSavedKey', {
      count,
      totalCount,
    }),
    [EMAIL_ACTION_MARK_AS_READ_ITEM]: t('Notification.EmailTitleMarkAsReadKey'),
    [EMAIL_ACTION_MARK_AS_READ_MASS]: t(
      'Notification.EmailTitleMassMarkAsReadKey',
      {
        count,
        totalCount,
      }
    ),
    [EMAIL_ACTION_MARK_AS_STARRED_ITEM]: t(
      'Notification.EmailTitleMarkAsStarredKey'
    ),
    [EMAIL_ACTION_MARK_AS_STARRED_MASS]: t(
      'Notification.EmailTitleMassMarkAsStarredKey',
      {
        count,
        totalCount,
      }
    ),
    [EMAIL_ACTION_MARK_AS_UNREAD_ITEM]: t(
      'Notification.EmailTitleMarkAsUnreadKey'
    ),
    [EMAIL_ACTION_MARK_AS_UNREAD_MASS]: t(
      'Notification.EmailTitleMassMarkAsUnreadKey',
      {
        count,
        totalCount,
      }
    ),
    [EMAIL_ACTION_MARK_AS_UNSTARRED_ITEM]: t(
      'Notification.EmailTitleMarkAsUnstarredKey'
    ),
    [EMAIL_ACTION_MARK_AS_UNSTARRED_MASS]: t(
      'Notification.EmailTitleMassMarkAsUnstarredKey',
      {
        count,
        totalCount,
      }
    ),
    [EMAIL_ACTION_RESTORE_ITEM]: t('Notification.CommonTitleRestoredKey'),
    [EMAIL_ACTION_RESTORE_MASS]: t('Notification.CommonTitleMassRestoredKey', {
      count,
      totalCount,
    }),
  }

  return types[action] || null
}

export function getEmailTableAsyncNotificationText({ action, t }) {
  const types = {
    [EMAIL_ACTION_ATTACH_REQUEST_MASS]: t(
      'Notification.EmailAttachRequestAsyncTextMassKey'
    ),
    [EMAIL_ACTION_COPY_TO_ARCHIVE_ITEM]: t(
      'Notification.EmailCopyToAsyncTextMassKey'
    ),
    [EMAIL_ACTION_COPY_TO_ARCHIVE_MASS]: t(
      'Notification.EmailCopyToAsyncTextMassKey'
    ),
    [EMAIL_ACTION_EDIT_LABELS_MASS]: t(
      'Notification.EmailAsyncTextMassEditLabelsKey'
    ),
  }

  return types[action] || null
}

export function getEmailTableItemActions({ item, sub }) {
  const { permissions, starred, unread } = item

  const isArchiveOrTrashSub = isEmailArchiveOrTrashSub(sub)

  const items = [
    {
      label: 'Emails.OpenInNewTabActionOptionKey',
      permission: true,
      value: EMAIL_ACTION_OPEN_IN_NEW_TAB,
    },
    {
      label: 'Emails.CreateServiceRequestActionOptionKey',
      permission: permissions.can_create_request,
      value: EMAIL_ACTION_CREATE_SERVICE_REQUEST,
    },
    {
      label: 'Emails.AttachServiceRequestActionOptionKey',
      permission: permissions.can_attach_to_request,
      value: EMAIL_ACTION_ATTACH_REQUEST,
    },
    {
      label: 'Emails.MarkAsUnreadActionOptionKey',
      permission: permissions.can_edit_unread_status && !unread,
      value: EMAIL_ACTION_MARK_AS_UNREAD_ITEM,
    },
    {
      label: 'Emails.MarkAsReadActionOptionKey',
      permission: permissions.can_edit_unread_status && unread,
      value: EMAIL_ACTION_MARK_AS_READ_ITEM,
    },
    {
      label: 'Emails.MarkAsStarredActionOptionKey',
      permission:
        permissions.can_edit_starred && !isArchiveOrTrashSub && !starred,
      value: EMAIL_ACTION_MARK_AS_STARRED_ITEM,
    },
    {
      label: 'Emails.MarkAsUnstarredActionOptionKey',
      permission:
        permissions.can_edit_starred && !isArchiveOrTrashSub && starred,
      value: EMAIL_ACTION_MARK_AS_UNSTARRED_ITEM,
    },
    {
      label: 'Emails.AddNoteActionOptionKey',
      permission: permissions.can_edit_note,
      value: EMAIL_ACTION_ADD_NOTE,
    },
    {
      label: 'Emails.EditLabelsActionOptionKey',
      permission: permissions.can_edit_labels,
      value: EMAIL_ACTION_EDIT_LABELS_ITEM,
    },
    {
      label: 'Emails.ReplyActionOptionKey',
      permission: permissions.can_reply,
      value: EMAIL_PROFILE_ACTION_REPLY,
    },
    {
      label: 'Emails.ReplyAllActionOptionKey',
      permission: permissions.can_reply,
      value: EMAIL_PROFILE_ACTION_REPLY_ALL,
    },
    {
      label: 'Emails.ForwardActionOptionKey',
      permission: permissions.can_reply,
      value: EMAIL_PROFILE_ACTION_FORWARD,
    },
    {
      label: 'Emails.PrintActionOptionKey',
      permission: true,
      value: EMAIL_PROFILE_ACTION_PRINT,
    },
    {
      label: 'Emails.CopyToActionOptionKey',
      permission: permissions.can_copy_to_archive,
      value: EMAIL_ACTION_COPY_TO_ARCHIVE_ITEM,
    },
    null,
    {
      label: 'Emails.ArchiveActionOptionKey',
      permission: permissions.can_archive,
      value: EMAIL_ACTION_ARCHIVE_ITEM,
    },
    {
      label: 'Emails.RestoreActionOptionKey',
      permission: permissions.can_restore,
      value: EMAIL_ACTION_RESTORE_ITEM,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

// The list response carries no `can_edit_labels`; the row flag depends only on
// the account ownership (ID-161), so any loaded row answers for the list.
export function hasEmailTableEditLabelsPermission(items = EMPTY_LIST) {
  return items.some(item => item.permissions?.can_edit_labels)
}

export function getEmailTableMassActions({ listItems, permissions, sub, t }) {
  const isArchiveOrTrashSub = isEmailArchiveOrTrashSub(sub)

  const items = [
    {
      label: t('Emails.MarkAsUnreadActionOptionKey'),
      permission: permissions.can_edit_unread_status,
      value: EMAIL_ACTION_MARK_AS_UNREAD_MASS,
    },
    {
      label: t('Emails.MarkAsReadActionOptionKey'),
      permission: permissions.can_edit_unread_status,
      value: EMAIL_ACTION_MARK_AS_READ_MASS,
    },
    {
      label: t('Emails.MarkAsUnstarredActionOptionKey'),
      permission: permissions.can_edit_starred,
      value: EMAIL_ACTION_MARK_AS_UNSTARRED_MASS,
    },
    {
      label: t('Emails.MarkAsStarredActionOptionKey'),
      permission: permissions.can_edit_starred,
      value: EMAIL_ACTION_MARK_AS_STARRED_MASS,
    },
    {
      label: t('Emails.EditLabelsActionOptionKey'),
      permission: hasEmailTableEditLabelsPermission(listItems),
      value: EMAIL_ACTION_EDIT_LABELS_MASS,
    },
    // Used only in single email view
    {
      label: t('Emails.AttachRequestActionOptionKey'),
      permission: permissions.can_attach_to_request,
      value: EMAIL_ACTION_ATTACH_REQUEST_MASS,
    },
    // Used only in single email view
    {
      label: t('Emails.CopyToActionOptionKey'),
      permission: permissions.can_copy_to_archive,
      value: EMAIL_ACTION_COPY_TO_ARCHIVE_MASS,
    },
    null,
    {
      label: t('Emails.ArchiveActionOptionKey'),
      permission: permissions.can_archive && !isArchiveOrTrashSub,
      value: EMAIL_ACTION_ARCHIVE_MASS,
    },
    {
      label: t('Emails.RestoreActionOptionKey'),
      permission: permissions.can_restore && isArchiveOrTrashSub,
      value: EMAIL_ACTION_RESTORE_MASS,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}
